import { create } from "zustand";
import api from '../apis/api';

// fetch the membership view, then merge CIP dates and tickets onto each row
export const fetchMembershipView = async (view_type, ids) => {
    const r = await api.post('/membership/view', { view_type, ids })
    const rows = view_type === "group" ? r.data.memberships : r.data
    const approverSiteIds = await getApproverSiteIds()
    rows.forEach(m => {
        m.isApprover = m.groupInfo.siteId.some(s => approverSiteIds.includes(s))
    })
    const membershipIds = rows.map(m => m.id)
    if (membershipIds.length > 0) {
        const [cip, tickets] = await Promise.allSettled([
            api.post('/membership/view/cip_dates', { membershipIds }),
            api.post('/membership/view/tickets', { membershipIds }),
        ])
        if (cip.status === "rejected") console.error(cip.reason)
        if (tickets.status === "rejected") console.error(tickets.reason)
        rows.forEach(m => {
            if (cip.status === "fulfilled" && cip.value.data[m.id]) m.cip = cip.value.data[m.id]
            m.tickets = tickets.status === "fulfilled" ? (tickets.value.data[m.id] ?? []) : []
        })
    }
    return r.data
}

export const useMembershipStore = create((set, get) => ({
    memberships: [],
    user_history: null,
    selected_users: [],
    selected_groups: [],
    selected_group_info: [],
    membership_modal: false,
    action: null,
    loading: false,
    reason: "",
    selected_memberships: {},
    user_cip_date_refresh: false,
    timers: {},
    approver_site_ids: [],
    approver_sites_promise: null,

    set_selected_memberships: (selected_memberships) =>
        set((state) => ({
            selected_memberships:
                typeof selected_memberships === "function"
                    ? selected_memberships(state.selected_memberships)
                    : selected_memberships,
        })),
    set_loading: (loading) => set({ loading }),
    set_membership_modal: (membership_modal) => set({ membership_modal }),
    set_selected_users: (selected_users) => set({ selected_users }),
    set_selected_groups: (selected_groups) => set({ selected_groups }),
    set_action: (action) => set({ action }),
    set_reason: (reason) => set({ reason }),
    reset_membership_modal_state: () => set({
        selected_users: [],
        selected_groups: [],
        selected_group_info: [],
        action: null,
        reason: "",
    }),
    open_membership_modal: () => set({
        membership_modal: true,
        selected_users: [],
        selected_groups: [],
        selected_group_info: [],
        action: null,
        reason: "",
    }),
    on_membership_update: (action, selected_users, selected_groups, reason = "", selected_group_info = []) => {
        set({ membership_modal: true, action, selected_users, selected_groups, reason, selected_group_info })
    },

    membership_query: null,
    membership_counts: {},
    membership_counts_query: null,
    get_memberships: (view_type, ids) => {
        return fetchMembershipView(view_type, ids).then(data => {
            set({ memberships: data, membership_query: { view_type: view_type, ids } })
        }).catch(err => console.error(err))
    },
    get_membership_counts: (field, ids, filters = {}) => {
        if (ids.length === 0) return
        const countsQuery = { field, ids, ...filters }
        set({ membership_counts_query: countsQuery })
        api.post('/membership/counts', countsQuery).then(r => {
            set({ membership_counts: r.data })
        }).catch(err => console.error(err))
    },
    refresh_memberships: () => {
        const query = get().membership_query
        const countsQuery = get().membership_counts_query
        const refreshPromise = query
            ? fetchMembershipView(query.view_type, query.ids).then(data => {
                set({ memberships: data })
            }).catch(err => console.error(err))
            : Promise.resolve()

        if (countsQuery?.ids?.length) {
            get().get_membership_counts(
                countsQuery.field,
                countsQuery.ids,
                { groupIds: countsQuery.groupIds }
            )
        }
        set({ user_cip_date_refresh: !get().user_cip_date_refresh })
        get().get_timers()
        return refreshPromise
    },
    get_approver_sites: () => {
        const promise = api.get('/membership/approver_sites').then(r => {
            set({ approver_site_ids: r.data.siteIds })
            return r.data.siteIds
        }).catch(err => {
            console.error(err)
            return []
        })
        set({ approver_sites_promise: promise })
        return promise
    },
    get_timers: () => {
        api.get('/membership/pending_email').then(r => {
            const timers = {}
            Object.entries(r.data?.sites || {}).forEach(([id, send_after]) => {
                timers[id] = send_after * 1000
            })
            set({ timers })
        }).catch(err => console.error(err))
    },
    get_user_history: (userId) => {
        set({ user_history: null })
        api.get('/membership/history/user', { params: { user_id: userId } }).then(r => {
            set({ user_history: r.data })
        }).catch(err => console.error(err))
    },
}))

export const getApproverSiteIds = () => {
    const state = useMembershipStore.getState()
    return state.approver_sites_promise ?? state.get_approver_sites()
}